import type { AudiencePreviewRow, AudienceReachabilityPreview, ContactRecord, SegmentFilter } from '@workspace/types';

export function reachableContactIds(rows: AudiencePreviewRow[] | undefined): string[] {
  if (!rows) {
    return [];
  }
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const id = row.contactId?.slice(0, 64);
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function excludedContactIdsFromPreview(
  rows: AudiencePreviewRow[] | undefined,
  includedIds: string[],
): string[] {
  const included = new Set(includedIds);
  return reachableContactIds(rows).filter(id => !included.has(id));
}

export function previewWithInclusions(
  preview: AudienceReachabilityPreview,
  includedIds: string[],
): AudienceReachabilityPreview {
  const rows = preview.reachablePreview ?? [];
  const included = new Set(includedIds);
  const shownSelected = rows.filter(row => !row.contactId || included.has(row.contactId)).length;
  const hidden = Math.max(0, preview.reachableCount - rows.length);
  const reachableCount = shownSelected + hidden;
  // Al desmarcar alcanzables baja "en segmento" en la misma cantidad; el resto del
  // total (los que quedan afuera por falta de dato, sea teléfono o email) no cambia.
  const deselected = Math.max(0, preview.reachableCount - reachableCount);
  return {
    ...preview,
    reachableCount,
    afterFiltersCount: Math.max(0, preview.afterFiltersCount - deselected),
  };
}

export function toggleIncludedId(includedIds: string[], contactId: string): string[] {
  if (includedIds.includes(contactId)) {
    return includedIds.filter(id => id !== contactId);
  }
  return [...includedIds, contactId];
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function sumPreviewBalance(rows: AudiencePreviewRow[] | undefined): number {
  if (!rows?.length) {
    return 0;
  }
  return roundMoney(rows.reduce((sum, row) => sum + row.totalBalance, 0));
}

export function selectedPreviewBalance(
  rows: AudiencePreviewRow[] | undefined,
  includedIds: string[] | undefined,
  fullTotal?: number,
): number {
  const list = rows ?? [];
  if (includedIds === undefined) {
    return fullTotal && fullTotal > 0 ? fullTotal : sumPreviewBalance(list);
  }
  const included = new Set(includedIds);
  const includedSum = sumPreviewBalance(list.filter(row => row.contactId && included.has(row.contactId)));
  if (fullTotal === undefined) {
    return includedSum;
  }
  const visibleSum = sumPreviewBalance(list);
  return roundMoney(fullTotal - (visibleSum - includedSum));
}

const MAX_TOTAL_BALANCE = 1_000_000_000_000;
const MAX_INVOICE_COUNT = 9999;

export function cobranzasSegmentActive(filter?: SegmentFilter | null): boolean {
  if (!filter) {
    return false;
  }
  return (
    filter.overdueOnly === true ||
    filter.minBalance !== undefined ||
    filter.minTotalBalance !== undefined ||
    filter.maxTotalBalance !== undefined ||
    filter.minInvoiceCount !== undefined ||
    filter.maxInvoiceCount !== undefined ||
    Boolean(filter.branch?.trim()) ||
    Boolean(filter.seller?.trim())
  );
}

export function contactMatchesCobranzasSegment(contact: ContactRecord, filter: SegmentFilter): boolean {
  const overdueOnly = filter.overdueOnly !== false;
  const totals = contactInvoiceTotals(contact.debts, overdueOnly);
  if (overdueOnly && totals.invoiceCount < 1) {
    return false;
  }
  const minTotal = filter.minTotalBalance ?? filter.minBalance ?? 0;
  let maxTotal = filter.maxTotalBalance ?? MAX_TOTAL_BALANCE;
  if (maxTotal < minTotal) {
    maxTotal = minTotal;
  }
  const minInvoices = filter.minInvoiceCount ?? 1;
  let maxInvoices = filter.maxInvoiceCount ?? MAX_INVOICE_COUNT;
  if (maxInvoices < minInvoices) {
    maxInvoices = minInvoices;
  }
  if (totals.totalBalance < minTotal || totals.totalBalance > maxTotal) {
    return false;
  }
  if (totals.invoiceCount < minInvoices || totals.invoiceCount > maxInvoices) {
    return false;
  }
  if (filter.excludedContactIds?.includes(contact.id)) {
    return false;
  }
  return true;
}

export function contactInvoiceTotals(
  debts: Array<{ balance: string; dueDate: string }> | undefined,
  overdueOnly: boolean,
): { totalBalance: number; invoiceCount: number } {
  if (!debts?.length) {
    return { totalBalance: 0, invoiceCount: 0 };
  }
  const today = new Date().toISOString().slice(0, 10);
  let totalBalance = 0;
  let invoiceCount = 0;
  for (const debt of debts) {
    const balance = Number(debt.balance);
    if (!Number.isFinite(balance) || balance <= 0) {
      continue;
    }
    if (overdueOnly && debt.dueDate.slice(0, 10) >= today) {
      continue;
    }
    totalBalance += balance;
    invoiceCount += 1;
  }
  return { totalBalance: roundMoney(totalBalance), invoiceCount };
}
